// src/shared/theme/colorUtils.ts
//
// Derivações de cor para a ponte DS ↔ white-label (Fase 1).
// A cor de marca (primary) do tenant é DINÂMICA — chega como hex do color-picker
// (ou já resolvida na cascata) e aqui derivamos hover/active/subtle/on-primary/etc.
//
// Regras:
// - Entrada canônica = hex (#rgb | #rrggbb). É o formato que os pickers e os
//   base themes usam para `primary`/`success`/`warning`/`error`/`info`.
// - Para entradas que NÃO são hex (rgba(), hsl(), nomes), degradamos com
//   segurança: alpha() devolve a cor como veio, darken/lighten idem, contrastOn
//   assume fundo claro. Isso nunca acontece com os tokens de marca reais, mas
//   protege contra overrides exóticos sem quebrar o paint.

interface RGB {
  r: number
  g: number
  b: number
}

/** Parseia hex (#rgb, #rrggbb, com/sem #). Retorna null se não for hex. */
function parseHex(input: string): RGB | null {
  const hex = input.trim().replace(/^#/, '')
  if (hex.length === 3) {
    const r = hex[0], g = hex[1], b = hex[2]
    if (!/^[0-9a-fA-F]{3}$/.test(hex)) return null
    return {
      r: parseInt(`${r}${r}`, 16),
      g: parseInt(`${g}${g}`, 16),
      b: parseInt(`${b}${b}`, 16),
    }
  }
  if (hex.length === 6) {
    if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    }
  }
  return null
}

const clamp = (n: number): number => Math.max(0, Math.min(255, Math.round(n)))

function toRgbString({ r, g, b }: RGB): string {
  return `rgb(${clamp(r)}, ${clamp(g)}, ${clamp(b)})`
}

/**
 * Devolve `color` com opacidade `a` (0..1).
 * Hex → rgba(); qualquer outra coisa → a própria cor (degradação segura).
 */
export function alpha(color: string, a: number): string {
  const rgb = parseHex(color)
  if (!rgb) return color
  const clamped = Math.max(0, Math.min(1, a))
  return `rgba(${clamp(rgb.r)}, ${clamp(rgb.g)}, ${clamp(rgb.b)}, ${clamped})`
}

/** Escurece `color` em `amount` (0..1) misturando com preto. Não-hex → inalterado. */
export function darken(color: string, amount: number): string {
  const rgb = parseHex(color)
  if (!rgb) return color
  const f = 1 - Math.max(0, Math.min(1, amount))
  return toRgbString({ r: rgb.r * f, g: rgb.g * f, b: rgb.b * f })
}

/** Clareia `color` em `amount` (0..1) misturando com branco. Não-hex → inalterado. */
export function lighten(color: string, amount: number): string {
  const rgb = parseHex(color)
  if (!rgb) return color
  const t = Math.max(0, Math.min(1, amount))
  return toRgbString({
    r: rgb.r + (255 - rgb.r) * t,
    g: rgb.g + (255 - rgb.g) * t,
    b: rgb.b + (255 - rgb.b) * t,
  })
}

/**
 * Escolhe texto legível sobre `bg` (YIQ). Protege marcas claras (amarelo/lima):
 * fundo claro → texto escuro; fundo escuro → branco.
 * Não-hex → branco (caso da maioria das marcas).
 */
export function contrastOn(bg: string, dark = '#1e293b', light = '#ffffff'): string {
  const rgb = parseHex(bg)
  if (!rgb) return light
  const yiq = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000
  return yiq >= 150 ? dark : light
}
